import { MediaCaptureAudioLevel, Video } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import React, { useEffect, useMemo, useState } from "react";
import { PlayerContainerRenderer } from "./interfaces";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { useMeetingSources } from "../../hooks/meeting";
import { MeetingPlayerDataOverlay } from "./MeetingPlayerDataOverlay";

import styles from "./VideoPlayer.module.css";

export interface VideoPlayerProps {
  src: MediaStream;
  poster?: string;
  muted?: boolean;
  videoStyle?: React.CSSProperties;
  renderCustomContainer?: PlayerContainerRenderer;
  /**
   * Meeting context of the played stream. When provided, the publication state
   * of the media session is displayed over the video, the same way the remote
   * players do it — this is how a local stream reports on its own emission.
   */
  meeting?: Meeting;
  session?: MeetingSession;
  mediaSession?: MeetingSessionMedia;
  /** The stream is our own outgoing media, previewed locally. */
  local?: boolean;
}

const EMPTY_STYLE: React.CSSProperties = {};

/**
 * How long a freshly captured track is kept out of the analysis. A capture
 * starts before its echo canceller has converged, so whatever the room is
 * playing leaks into the first samples: a burst that jolts the meter, and that
 * marks the source as having heard sound — which is what the no-sound warning
 * waits on, so it would never show again.
 */
const AUDIO_LEVEL_WARMUP = 1500;

export const VideoPlayer = React.memo((
  { src, poster, muted, videoStyle, renderCustomContainer, meeting, session, mediaSession, local }: VideoPlayerProps
) => {
  const mergedStyle = useMemo<React.CSSProperties>(
    () => ({ width: "100%", height: "100%", ...(videoStyle ?? EMPTY_STYLE) }),
    [videoStyle]
  );

  // Only a local capture can be missing a device: a reception knows nothing of
  // what is plugged in at the other end. Sources are keyed by media name.
  const devices = useAppStoreSelector(
    (state) => (local && mediaSession?.name ? state.sourceDevices[mediaSession.name] : undefined)
  );

  // Audio level of our own capture. Read off the track rather than off `src`:
  // a capture keeps the same stream for its whole life and swaps the tracks
  // inside it, which the analyzer has no way to notice. The store only holds
  // one while the source really captures from a device, so a source with no
  // audio — or fallen back to the silent track — simply has no meter.
  const audioTrack = useAppStoreSelector(
    (state) => (local && mediaSession?.name ? state.selfAudioTracks[mediaSession.name] : undefined)
  );
  const audioLevelInput = useMemo(() => (audioTrack ? new MediaStream([audioTrack]) : undefined), [audioTrack]);

  const [analyzeAudioLevel, setAnalyzeAudioLevel] = useState(false);
  useEffect(() => {
    setAnalyzeAudioLevel(false);
    if (!audioLevelInput) return;
    const timeout = setTimeout(() => setAnalyzeAudioLevel(true), AUDIO_LEVEL_WARMUP);
    return () => clearTimeout(timeout);
  }, [audioLevelInput]);
  const audioLevelAnalyzerOptions = useMemo(() => ({ enabled: analyzeAudioLevel }), [analyzeAudioLevel]);

  // Meter settings of the source this media comes from. Medias are named after
  // their source, which is what ties the two back together here.
  const sources = useMeetingSources();
  const audioLevel = useMemo(
    () => (mediaSession?.name ? sources.find((s) => s.name === mediaSession.name)?.audioLevel : undefined),
    [sources, mediaSession?.name],
  );

  const renderContent = () => {
    const video = <Video poster={poster} muted={muted} style={mergedStyle} src={src} autoPlay playsInline />;
    // No meeting context: keep the bare video, the overlay has nothing to report
    if (!meeting || !session) return video;
    return (
      <div className={styles.container}>
        {video}
        <MeetingPlayerDataOverlay
          meeting={meeting}
          session={session}
          mediaSession={mediaSession}
          mediaStream={src}
          local={local}
          noVideoDevice={devices ? !devices.video : false}
          noAudioDevice={devices ? !devices.audio : false}
        />
        {audioLevelInput && !audioLevel?.disabled && (
          <MediaCaptureAudioLevel
            className={styles.audioLevel}
            input={audioLevelInput}
            audioContext={getAudioContextForVolumeAnalyzer()}
            audioVolumeAnalyzerOptions={audioLevelAnalyzerOptions}
            // Greyed out rather than hidden while the media is muted, the way
            // the meter of a capture behaves when its audio is turned off
            enabled={mediaSession?.audio.enabled !== false}
            // Left undefined, each one falls back to the component's own default
            boost={audioLevel?.boost}
            minVolume={audioLevel?.minVolume}
            maxVolume={audioLevel?.maxVolume}
            displayNoSoundWarning={audioLevel?.displayNoSoundWarning ?? true }
            noSoundMessage={audioLevel?.noSoundMessage}
          />
        )}
      </div>
    );
  };
  if (renderCustomContainer) {
    return <>{renderCustomContainer(renderContent())}</>;
  }
  return renderContent();
});
